import { ChangeDetectionStrategy, Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Drawer } from 'primeng/drawer';
import { CartService } from '../../services/cart.service';
import { I18nService } from '../../services/i18n.service';
import { CartItemDto } from '../../../core/api/_dtos/cart.dto';
import { AppButton } from '../../../features/client/components/atom/app-button/app-button';
import { ProductPreviewImageComponent } from '../product-preview-image/product-preview-image';
import { buildCartEditLink, CartEditLinkParts } from '../../helpers/cart-edit-link.helper';

@Component({
  selector: 'app-cart-dropdown',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, Drawer, AppButton, ProductPreviewImageComponent],
  templateUrl: './cart-dropdown.component.html',
  styleUrl: './cart-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartDropdownComponent {
  isOpen = false;

  constructor(
    public cartService: CartService,
    public i18n: I18nService,
    private router: Router
  ) {
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  onDrawerHide(): void {
    this.isOpen = false;
  }

  onQuantityChange(item: CartItemDto, newQuantity: number): void {
    if (newQuantity < 1) return;
    this.cartService.updateQuantity(item.id, newQuantity).subscribe();
  }

  onQuantityInputChange(item: CartItemDto, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number(input.value);
    if (!Number.isFinite(value)) {
      input.value = String(item.quantity);
      return;
    }
    const nextQuantity = Math.max(1, Math.floor(value));
    input.value = String(nextQuantity);
    if (nextQuantity === item.quantity) return;
    this.cartService.updateQuantity(item.id, nextQuantity).subscribe();
  }

  /**
   * Track items by their unique id so Angular reuses DOM nodes correctly
   * and native input [value] stays in sync with the signal-driven data.
   */
  trackByItemId(_index: number, item: CartItemDto): string {
    return item.id;
  }

  onRemoveItem(item: CartItemDto): void {
    this.cartService.removeItem(item.id).subscribe();
  }

  getEditLink(item: CartItemDto): CartEditLinkParts {
    return buildCartEditLink(item, this.i18n.generateUrlWithLang('/project'));
  }

  onEditItem(item: CartItemDto): void {
    const link = this.getEditLink(item);
    this.closeDropdown();
    this.router.navigate([link.routerLink], { queryParams: link.queryParams });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.cart-dropdown') && !target.closest('.p-drawer')) {
      this.closeDropdown();
    }
  }
}

